import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { faqs, howItWorks, purchaseSeatsLanguages } from "./helpers";
import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import StaticSeatMap from "public/images/midlandrockhounds_flex/RockHoundsMap.png";
import FindGamesBannerImage from "public/images/midlandrockhounds_flex/Flex Plan Banner 2025.jpg";
import LogoImage from "public/images/midlandrockhounds_flex/Midland_RockHounds.png";
import type { TeamConfig } from "@/global/hooks/useTeamConfig";
import Link from "@mui/material/Link";

const config: TeamConfig = {
  teamSchema: "midlandrockhounds_flex",
  browserTitle: "RockHounds Build Your Own Flex Plan",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "", // Optional, set to empty string
  teamName: "Midland Rockhounds",

  // Seat map configs
  seatMapConfigs: {
    venueId: "", // Not available
    mapId: "", // Not available
    staticSeatMapImage: StaticSeatMap.src, // Use the static seat map image
    isShowAvailableSeatsButtonHidden: true,
    isView3dButtonHidden: true,
    customSeatMapStaticImageStyle: {
      width: "80%",
      display: "block",
      marginX: "auto",
    },
  },

  // Gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: "",
    redeemSuccessModalLink: "",
    hideRedeemItem: false,
    customShareGiftUrl: "",
    customErrorMessage: "",
    hideRecipient: false,
  },

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        discount: 10,
        customProgressBarMessage:
          "Congrats! You've unlocked 10% off your tickets! Add 2 more games for 20% off, 4+ more games for 30% off & an exclusive Midland Rockhounds Crossbody Bag or checkout now.",
      },
      {
        id: 2,
        minSelectedAmount: 3,
        updatedAccountGroup: 0,
        discount: 10,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Add 1 more game for 20% off or add 4+ more games for 30% off & an exclusive Midland Rockhounds Crossbody Bag.",
      },
      {
        id: 3,
        minSelectedAmount: 4,
        updatedAccountGroup: 1,
        discount: 20,
        customProgressBarMessage:
          "Congrats! You have unlocked 20% off your tickets! Add 2+ more games for 30% off & an exclusive Midland Rockhounds Crossbody Bag, or checkout now.",
      },
      {
        id: 4,
        minSelectedAmount: 5,
        updatedAccountGroup: 1,
        discount: 20,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Add 1 more game for 30% off & an exclusive Midland Rockhounds Crossbody Bag.",
      },
      {
        id: 5,
        minSelectedAmount: 6,
        updatedAccountGroup: 2,
        discount: 30,
        customAditionalMessage:
          " for 30% off & an exclusive Midland Rockhounds Crossbody Bag?",
        customProgressBarMessage:
          "Congrats! You have unlocked 30% off your tickets & an exclusive Midland Rockhounds Crossbody Bag! Add more games or checkout now.",
      },
    ],
  },

  // Purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // Disabled routes
  disabledRoutes: [routes.giftCard],

  // Switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // Purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 9,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isDisabledEventSelection: false,
    hideAllUpcomingGames: false,
    isRepProduct: false,
  },

  // Feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // Intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // Checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity, // No limit specified
    allowedPaymentFailCount: Infinity, // No limit specified
  },

  isSaveCardAllowed: true,
  isBrainTreePaymentEnabled: false,
  shouldUseInternalLogin: false,
  oktaClientId: "0oasmcdgh92Jom7TI357",
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  platform: vendorUUIDs.tickesdotcom, // TDC platform
  refreshInventoryWhenEventClick: false,

  // Analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "",
  },

  // Redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // URLs
  accountManagerUrl:
    "https://mlb.tickets.com/?agency=MRHM_MYTIXX&orgid=56878#/auth/login",

  // Images
  images: {
    findGamesBannerImage: FindGamesBannerImage.src, // Replace with actual image source
    authBackgroundImage: FindGamesBannerImage.src, // Using findGamesBannerImage
    checkoutStatusPageImage: FindGamesBannerImage.src,
    logoImage: LogoImage.src + "?width=128&height=75", // actual size 330x295 Replace with actual image source
    logoNameImage: "",
    faviconImage: LogoImage.src,
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },
    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },
    progressBar: {
      staticProgressBarText: false,
      customProgressBarSize: 600,
    },
    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:4325202255"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (432) 520-2255
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#0069aa",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: purchaseSeatsLanguages,
    // Gift card languages are empty since gift cards are disabled
    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,
    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Midland Rockhounds tickets are confirmed! Your tickets will be available within your MyProvenue Account shortly.",
    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
